import {
  useFloating,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  useDelayGroup,
  useDelayGroupContext
} from "@floating-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { createContext, useContext, useRef, useState } from "react";

/* ---------------- CONTEXT ---------------- */

const TooltipContext = createContext(null);

export function useTooltip() {
  return useContext(TooltipContext);
}

/* ---------------- PROVIDER (for delay groups) ---------------- */

export function TooltipProvider({ children, delay = 200 }) {
  const group = useDelayGroup({ delay });
  return (
    <TooltipContext.Provider value={group}>
      {children}
    </TooltipContext.Provider>
  );
}

/* ---------------- ROOT ---------------- */

export function Tooltip({
  children,
  open: controlledOpen,
  onOpenChange,
  placement = "top",
  trigger = "hover",
  offsetValue = 10,
  followCursor = false
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const arrowRef = useRef(null);

  const group = useTooltip();
  const { delay } = useDelayGroupContext(group);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [
      offset(offsetValue),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef })
    ],
  });

  const hover = useHover(context, {
    enabled: trigger === "hover",
    delay,
    move: followCursor
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  return (
    <TooltipContext.Provider
      value={{
        open,
        setOpen,
        refs,
        floatingStyles,
        middlewareData,
        arrowRef,
        getReferenceProps,
        getFloatingProps,
        trigger
      }}
    >
      {children}
    </TooltipContext.Provider>
  );
}

/* ---------------- TRIGGER ---------------- */

export function TooltipTrigger({ children, className = "" }) {
  const { refs, getReferenceProps, trigger, open, setOpen } = useTooltip();

  return (
    <div
      ref={refs.setReference}
      {...getReferenceProps()}
      className={`inline-block w-fit ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        if (trigger === "click") setOpen(!open);
      }}
    >
      {children}
    </div>
  );
}

/* ---------------- CONTENT ---------------- */

export function TooltipContent({
  children,
  className = "",
  arrowClass = "",
  showArrow = true,
  animation = "scale"
}) {
  const {
    open,
    refs,
    floatingStyles,
    getFloatingProps,
    middlewareData,
    arrowRef
  } = useTooltip();

  if (!open) return null; // ✅ prevents initial wrong render

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        {...getFloatingProps()}
        style={{
          ...floatingStyles,
          position: "absolute", // ✅ IMPORTANT FIX
          top: floatingStyles?.top ?? 0,
          left: floatingStyles?.left ?? 0
        }}
        className={`z-50 ${className}`}
      >
        {children}

        {showArrow && (
          <div
            ref={arrowRef}
            className={`absolute w-3 h-3 rotate-45 ${arrowClass}`}
            style={{
              left: middlewareData.arrow?.x ?? "",
              top: middlewareData.arrow?.y ?? ""
            }}
          />
        )}
      </div>
    </FloatingPortal>
  );
}